import React from 'react';
import { RefreshCw, AlertTriangle, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { type McProjectionState } from '@/hooks/useMcProjectionState';

interface McProjectionBadgeProps {
  state: McProjectionState;
  className?: string;
  /** Optional: show simulation identity info */
  triggeredBy?: string | null;
  versaoLogica?: number | null;
}

const sourceLabels: Record<string, string> = {
  manual: 'Manual',
  auto_queue: 'Automático',
  boletim_aprovado: 'Aprovação de boletim',
  boletim_aprovado_para_faturamento: 'Aprovação de boletim',
  boletim_rejeitado: 'Rejeição de boletim',
  baseline_ativada: 'Nova baseline',
  contrato_data_fim: 'Alteração de prazo',
  aditivo_aprovado: 'Aditivo aprovado',
  cronograma_aprovado: 'Cronograma aprovado',
  planilha_ativada: 'Planilha ativada',
  validacao_insert: 'Nova execução',
  validacao_update: 'Alteração de execução',
  curva_s_snapshot: 'Snapshot curva S',
};

/**
 * Visual indicator for Monte Carlo projection consistency state.
 * FRESH → optional identity info
 * STALE → animated "Atualizando..." badge
 * ERROR → warning badge
 */
export const McProjectionBadge: React.FC<McProjectionBadgeProps> = ({
  state, className, triggeredBy, versaoLogica,
}) => {
  if (state === 'stale') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="secondary" className={`gap-1 text-xs font-normal ${className || ''}`}>
            <RefreshCw className="h-3 w-3 animate-spin" />
            Atualizando projeção...
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs max-w-[220px]">
            Uma mudança foi detectada. A projeção Monte Carlo está sendo recalculada automaticamente.
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (state === 'error') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="destructive" className={`gap-1 text-xs font-normal ${className || ''}`}>
            <AlertTriangle className="h-3 w-3" />
            Erro na atualização
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs max-w-[220px]">
            A última tentativa de recálculo falhou. O sistema tentará novamente em breve.
            Use "Forçar Recálculo" se necessário.
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // FRESH: show identity badge if available
  if (triggeredBy && versaoLogica) {
    const label = sourceLabels[triggeredBy] || triggeredBy;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`gap-1 text-xs font-normal text-muted-foreground ${className || ''}`}>
            <Info className="h-3 w-3" />
            v{versaoLogica}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs max-w-[220px]">
            Simulação v{versaoLogica} — Gerada por: {label}
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return null;
};
